import React, { useCallback, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Transition } from '@headlessui/react';
import {
  PiArrowUpLight,
  PiCheckCircleLight,
  PiUserPlusLight,
  PiUserMinusLight,
  PiFileTextLight,
  PiClockLight,
  PiArrowsClockwiseLight,
  PiFunnelLight,
  PiCaretDownLight,
  PiFlagLight,
  PiCalendarBlankLight,
  PiArrowsLeftRightLight,
  PiPushPinLight,
  PiWarningLight,
  PiNoteLight,
  PiTargetLight,
  PiChatCircleLight,
} from 'react-icons/pi';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import ProfileImage from '../../Header/ProfileImage';
import FeedSkeleton from './FeedSkeleton';
import { renderMessageWithMentions } from './MentionParser';
import MentionInput from './MentionInput';
import { sanitizeString } from '../../../utils/validation/sanitizers';
import { formatEventType } from '../../../utils/functions';

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  profile_picture?: string | null;
}

interface CreatedBy {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  profile_picture?: string | null;
}

interface FeedComment {
  id?: string;
  message: string;
  timestamp?: string;
  created_by_user_id?: string | number;
  created_by?: CreatedBy;
  isOptimistic?: boolean;
}

interface FeedEvent {
  id?: string;
  event_type: string;
  message: string;
  timestamp?: string;
  created_by_user_id?: string | number;
  created_by_name?: string;
  created_by?: CreatedBy;
  metadata?: any;
  member?: Member;
  comments?: FeedComment[];
  isOptimistic?: boolean;
}

interface CurrentUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface FeedComponentProps {
  events: FeedEvent[];
  loading?: boolean;
  error?: string;
  onRefresh: () => Promise<void>;
  onPostEvent?: (eventData: { event_type: string; message: string; metadata?: any }) => Promise<void>;
  currentUser?: CurrentUser;
  teamMembers?: Array<{ id: string; first_name: string; last_name: string; email: string }>;
  onCommentAdd?: (eventId: string, comment: string) => Promise<void>;
}

const getEventIcon = (eventType: string) => {
  switch (eventType) {
    case 'team_created':
    case 'department_created':
    case 'task_created':
      return <PiFileTextLight className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    case 'member_added':
    case 'collaborator_added':
      return <PiUserPlusLight className="w-5 h-5 text-green-600 dark:text-green-400" />;
    case 'member_removed':
    case 'collaborator_removed':
      return <PiUserMinusLight className="w-5 h-5 text-red-600 dark:text-red-400" />;
    case 'status_changed':
      return <PiArrowsLeftRightLight className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
    case 'priority_changed':
      return <PiFlagLight className="w-5 h-5 text-orange-600 dark:text-orange-400" />;
    case 'due_date_changed':
      return <PiCalendarBlankLight className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
    case 'description_updated':
    case 'update':
      return <PiCheckCircleLight className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
    case 'milestone':
      return <PiTargetLight className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    case 'issue':
      return <PiWarningLight className="w-5 h-5 text-orange-600 dark:text-orange-400" />;
    case 'note':
      return <PiNoteLight className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    case 'action':
      return <PiCheckCircleLight className="w-5 h-5 text-green-600 dark:text-green-400" />;
    default:
      return <PiFileTextLight className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
  }
};

const getEventLabel = (eventType: string) => {
  return formatEventType(eventType);
};

const getEventBadgeColor = (eventType: string): any => {
  switch (eventType) {
    case 'team_created':
    case 'department_created':
    case 'task_created':
    case 'milestone':
      return 'blue';
    case 'member_added':
    case 'collaborator_added':
    case 'action':
      return 'green';
    case 'member_removed':
    case 'collaborator_removed':
      return 'red';
    case 'description_updated':
    case 'update':
    case 'status_changed':
      return 'purple';
    case 'priority_changed':
    case 'issue':
      return 'orange';
    case 'due_date_changed':
      return 'yellow';
    default:
      return 'gray';
  }
};

/**
 * Parse a timestamp string ensuring UTC interpretation.
 * Backend stores all timestamps in UTC. If the string lacks a timezone
 * indicator (Z or +/-offset), we append 'Z' so the browser correctly
 * interprets it as UTC before converting to local time.
 */
const parseUTCTimestamp = (timestamp: string): Date => {
  const trimmed = timestamp.trim();
  // Already has timezone info
  if (/Z$/i.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }
  // Replace space separator with T for ISO compliance, then append Z
  const isoString = trimmed.replace(' ', 'T');
  return new Date(isoString + 'Z');
};

const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) return '';
  
  try {
    const date = parseUTCTimestamp(timestamp);
    if (isNaN(date.getTime())) return timestamp;

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // For older dates, show local date/time
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  } catch {
    return timestamp;
  }
};

const getUniqueEventTypes = (eventsList: FeedEvent[]): string[] => {
  const uniqueTypes = new Set<string>();
  eventsList.forEach(event => {
    if (event.event_type) {
      uniqueTypes.add(event.event_type);
    }
  });
  return Array.from(uniqueTypes).sort();
};

const FeedComponent: React.FC<FeedComponentProps> = ({
  events,
  loading = false,
  error = '',
  onRefresh,
  onPostEvent,
  currentUser,
  teamMembers = [],
  onCommentAdd,
}) => {
  const { t } = useTranslation();

  const defaultCurrentUser: CurrentUser = {
    id: 'current-user',
    first_name: 'You',
    last_name: '',
    email: '',
  };
  const [refreshing, setRefreshing] = useState(false);
  const [postMessage, setPostMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [postType, setPostType] = useState<string>('update');
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [eventComments, setEventComments] = useState<{ [key: string]: string }>({});
  const [commentingEventId, setCommentingEventId] = useState<string | undefined>(undefined);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPerson, setFilterPerson] = useState<string>('all');
  const [filterTime, setFilterTime] = useState<string>('all');
  const [showCommentMentions, setShowCommentMentions] = useState(false);
  const [commentMentionQuery, setCommentMentionQuery] = useState('');
  const [optimisticEvents, setOptimisticEvents] = useState<FeedEvent[]>([]);
  const [optimisticComments, setOptimisticComments] = useState<{ [key: string]: FeedComment[] }>({});

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  // Silent background refresh — no loading indicators, just merges new data
  const silentRefresh = useCallback(async () => {
    try {
      await onRefresh();
    } catch {
      // Swallow — optimistic item is already shown
    }
  }, [onRefresh]);

  const insertMention = (_memberId: string, memberName: string) => {
    const lastAtIndex = postMessage.lastIndexOf('@');
    const beforeMention = postMessage.substring(0, lastAtIndex);
    const afterQuery = postMessage.substring(lastAtIndex).split(/\s/);
    const afterMention = afterQuery.slice(1).join(' ');

    const newMessage = beforeMention + `@[${memberName}] ` + (afterMention ? afterMention + ' ' : '');
    setPostMessage(newMessage);
    setShowMentionSuggestions(false);
    setMentionQuery('');
  };

  const filteredMembers = teamMembers.filter(member =>
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const filteredCommentMembers = teamMembers.filter(member =>
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(commentMentionQuery.toLowerCase())
  );

  const getFilteredEvents = useCallback(() => {
    // Remove optimistic events that have been confirmed by the server
    // An optimistic event is confirmed if the real events contain a similar recent event with the same type and message
    const unconfirmedOptimistic = optimisticEvents.filter(optimisticEvent => {
      const hasConfirmed = events.some(realEvent =>
        realEvent.event_type === optimisticEvent.event_type &&
        realEvent.message === optimisticEvent.message &&
        realEvent.created_by?.id === optimisticEvent.created_by?.id
      );
      return !hasConfirmed;
    });

    // Auto-clean confirmed optimistic events from state
    if (unconfirmedOptimistic.length < optimisticEvents.length) {
      // Use setTimeout to avoid setState during render
      setTimeout(() => setOptimisticEvents(unconfirmedOptimistic), 0);
    }

    let filtered = [...unconfirmedOptimistic, ...events];

    // Filter by event type if not 'all'
    if (filterType !== 'all') {
      filtered = filtered.filter(e => e.event_type === filterType);
    }

    // Validate filterPerson is a valid team member ID (basic validation)
    if (filterPerson !== 'all') {
      filtered = filtered.filter(e => {
        // Only filter by person if it's a valid ID format
        return e.created_by?.id === filterPerson;
      });
    }

    if (filterTime !== 'all') {
      // Only allow whitelisted time filters
      const validTimeFilters = ['today', 'week', 'month', 'all'];
      if (validTimeFilters.includes(filterTime)) {
        const now = new Date();
        filtered = filtered.filter(e => {
          if (!e.timestamp && !e.isOptimistic) return true;
          const eventDate = e.timestamp ? parseUTCTimestamp(e.timestamp) : now;
          switch (filterTime) {
            case 'today':
              return eventDate.toDateString() === now.toDateString();
            case 'week':
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return eventDate >= weekAgo;
            case 'month':
              const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              return eventDate >= monthAgo;
            default:
              return true;
          }
        });
      }
    }

    return filtered;
  }, [events, optimisticEvents, filterType, filterPerson, filterTime]);

  /**
   * Get unconfirmed optimistic comments for a given event.
   * Filters out optimistic comments that already appear in the real server comments
   * (matched by message + created_by id), preventing duplicate display.
   */
  const getUnconfirmedOptimisticComments = useCallback((eventId: string, serverComments: FeedComment[] | undefined): FeedComment[] => {
    const pending = optimisticComments[eventId];
    if (!pending || pending.length === 0) return [];
    if (!serverComments || serverComments.length === 0) return pending;

    const unconfirmed = pending.filter(optimistic => {
      const hasConfirmed = serverComments.some(real =>
        real.message === optimistic.message &&
        real.created_by?.id === optimistic.created_by?.id
      );
      return !hasConfirmed;
    });

    // Auto-clean confirmed optimistic comments from state
    if (unconfirmed.length < pending.length) {
      setTimeout(() => {
        setOptimisticComments(prev => {
          const updated = { ...prev };
          if (unconfirmed.length === 0) {
            delete updated[eventId];
          } else {
            updated[eventId] = unconfirmed;
          }
          return updated;
        });
      }, 0);
    }

    return unconfirmed;
  }, [optimisticComments]);

  const handlePostEvent = useCallback(async () => {
    if (!postMessage.trim() || !onPostEvent) return;

    // Sanitize the message (trim + max length; no HTML escaping — React handles display escaping,
    // and the backend runs its own bleach.clean before storing)
    const sanitizedMessage = sanitizeString(postMessage.trim(), 5000, true);

    // Additional validation: ensure message is not empty after sanitization
    if (!sanitizedMessage.trim()) {
      console.warn('Message is empty after sanitization');
      return;
    }

    const userData = currentUser || defaultCurrentUser;
    const newEvent: FeedEvent = {
      id: `optimistic-${Date.now()}`,
      event_type: postType,
      message: sanitizedMessage,
      isOptimistic: true,
      created_by: {
        id: userData.id,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
      },
      timestamp: new Date().toISOString(),
    };

    setOptimisticEvents(prev => [newEvent, ...prev]);
    setPosting(true);

    try {
      await onPostEvent({
        event_type: postType,
        message: sanitizedMessage,
      });

      setPostMessage('');
      setPostType('update');

      // Silently refresh in background — the optimistic event stays visible
      // until the real event arrives and getFilteredEvents deduplicates it
      silentRefresh();
    } catch (err) {
      setOptimisticEvents(prev => prev.filter(e => e.id !== newEvent.id));
      console.error('Error posting event:', err);
    } finally {
      setPosting(false);
    }
  }, [postMessage, postType, onPostEvent, silentRefresh, currentUser, defaultCurrentUser]);

  const handleAddComment = useCallback(async (eventId: string, comment: string) => {
    if (!eventId || !comment?.trim() || !onCommentAdd) return;

    // Sanitize comment (trim + max length; no HTML escaping — backend handles that)
    const sanitizedComment = sanitizeString(comment.trim(), 5000, true);

    // Validate that comment is not empty after sanitization
    if (!sanitizedComment.trim()) {
      console.warn('Comment is empty after sanitization');
      return;
    }

    const userData = currentUser || defaultCurrentUser;
    const newComment: FeedComment = {
      id: `optimistic-${Date.now()}`,
      message: sanitizedComment,
      isOptimistic: true,
      created_by: {
        id: userData.id,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
      },
      timestamp: new Date().toISOString(),
    };

    setOptimisticComments(prev => ({
      ...prev,
      [eventId]: [...(prev[eventId] || []), newComment]
    }));

    setEventComments(prev => {
      const updated = { ...prev };
      delete updated[eventId];
      return updated;
    });

    try {
      await onCommentAdd(eventId, sanitizedComment);
      setCommentingEventId(undefined);

      // Silently refresh in background — optimistic comment stays visible
      // until the real comment arrives and deduplication removes it
      silentRefresh();
    } catch (err) {
      setOptimisticComments(prev => ({
        ...prev,
        [eventId]: (prev[eventId] || []).filter(c => c.id !== newComment.id)
      }));
      console.error('Error adding comment:', err);
    }
  }, [onCommentAdd, silentRefresh, currentUser, defaultCurrentUser]);

  if (error) {
    return (
      <div className="text-red-600 dark:text-red-400 p-4 text-center bg-red-50 dark:bg-red-900/20 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full gap-0 overflow-hidden">
      {/* Feed Header with Filters - Sticky at top */}
      <div className="flex justify-between items-center gap-4 flex-shrink-0 bg-background-light dark:bg-background-dark px-3 pb-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <PiArrowsClockwiseLight className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters - Right Side */}
        <div className="flex flex-wrap gap-1 items-center justify-end">
          {/* Type Filter */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex gap-1 items-center px-2 py-1 border border-gray-300 dark:border-gray-500 bg-transparent text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 rounded shadow-sm text-xs">
              <span className="text-black dark:text-white">{t('Type')}</span>
              <PiFunnelLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Menu.Items className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50 max-h-64 overflow-y-auto">
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={() => setFilterType('all')}
                      className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${
                        active ? 'bg-gray-100 dark:bg-gray-600' : ''
                      } ${filterType === 'all' ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 font-medium' : ''}`}
                    >
                      {t('All Types')}
                    </div>
                  )}
                </Menu.Item>
                {getUniqueEventTypes([...optimisticEvents, ...events]).map((eventType) => (
                  <Menu.Item key={eventType}>
                    {({ active }) => (
                      <div
                        onClick={() => setFilterType(eventType)}
                        className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${
                          active ? 'bg-gray-100 dark:bg-gray-600' : ''
                        } ${filterType === eventType ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 font-medium' : ''}`}
                      >
                        {formatEventType(eventType)}
                      </div>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>

          {/* Person Filter */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex gap-1 items-center px-2 py-1 border border-gray-300 dark:border-gray-500 bg-transparent text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 rounded shadow-sm text-xs">
              <span className="text-black dark:text-white">{t('Person')}</span>
              <PiFunnelLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Menu.Items className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50">
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={() => setFilterPerson('all')}
                      className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${
                        active ? 'bg-gray-100 dark:bg-gray-600' : ''
                      } ${filterPerson === 'all' ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 font-medium' : ''}`}
                    >
                      {t('All People')}
                    </div>
                  )}
                </Menu.Item>
                {teamMembers.map((member) => (
                  <Menu.Item key={member.id}>
                    {({ active }) => (
                      <div
                        onClick={() => setFilterPerson(member.id)}
                        className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${
                          active ? 'bg-gray-100 dark:bg-gray-600' : ''
                        } ${filterPerson === member.id ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 font-medium' : ''}`}
                      >
                        {member.first_name} {member.last_name}
                      </div>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>

          {/* Time Filter */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex gap-1 items-center px-2 py-1 border border-gray-300 dark:border-gray-500 bg-transparent text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 rounded shadow-sm text-xs">
              <span className="text-black dark:text-white">{t('Time')}</span>
              <PiFunnelLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Menu.Items className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50">
                {[
                  { value: 'all', label: 'All Time' },
                  { value: 'today', label: 'Today' },
                  { value: 'week', label: 'Last 7 Days' },
                  { value: 'month', label: 'Last 30 Days' },
                ].map((option) => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <div
                        onClick={() => setFilterTime(option.value)}
                        className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${
                          active ? 'bg-gray-100 dark:bg-gray-600' : ''
                        } ${filterTime === option.value ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 font-medium' : ''}`}
                      >
                        {t(option.label)}
                      </div>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>

          {/* Clear Filters */}
          {(filterType !== 'all' || filterPerson !== 'all' || filterTime !== 'all') && (
            <button
              onClick={() => {
                setFilterType('all');
                setFilterPerson('all');
                setFilterTime('all');
              }}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 px-2 py-1"
            >
              {t('Clear Filters')}
            </button>
          )}
        </div>
      </div>

      {/* Feed Events - Scrollable Container */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-330px)] min-h-[calc(100vh-330px)] custom-scrollbar min-h-0 p-3 space-y-3">
        {loading && !optimisticEvents.length && events.length === 0 ? (
          <FeedSkeleton />
        ) : getFilteredEvents().length > 0 ? (
          <AnimatePresence mode="popLayout">
            {getFilteredEvents().map((event, idx) => {
              const eventId = event.id || String(idx);
              const pendingComments = getUnconfirmedOptimisticComments(eventId, event.comments);
              const ownerId = event.created_by?.id ?? event.created_by_user_id;
              const isOwn = !!currentUser && ownerId !== undefined && String(ownerId) === String(currentUser.id);
              return (
                <motion.div
                  key={eventId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className={`flex w-full ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                <div
                  className={`w-full sm:w-[75%] bg-white dark:bg-gray-800 border rounded-xl overflow-hidden transition-shadow hover:shadow-md ${
                    isOwn
                      ? 'border-primary-300 dark:border-primary-700/60'
                      : 'border-gray-200 dark:border-gray-700'
                  } ${event.isOptimistic ? 'opacity-90' : ''} ${(event as any).is_pinned ? 'ring-1 ring-amber-300 dark:ring-amber-600' : ''}`}
                >
                  {/* Card header: avatar + name + badge + timestamp */}
                  <div className="flex items-center gap-2.5 px-4 py-3">
                    <div className="flex-shrink-0">
                      {event.created_by ? (
                        <ProfileImage
                          profilePicture={event.created_by.profile_picture || null}
                          email={event.created_by.email}
                          initials={`${event.created_by.first_name?.[0] || ''}${event.created_by.last_name?.[0] || ''}`.toUpperCase()}
                          size="w-8 h-8"
                          firstName={event.created_by.first_name}
                          lastName={event.created_by.last_name}
                        />
                      ) : event.member ? (
                        <ProfileImage
                          profilePicture={event.member.profile_picture || null}
                          email={event.member.email}
                          initials={`${event.member.first_name?.[0] || ''}${event.member.last_name?.[0] || ''}`.toUpperCase()}
                          size="w-8 h-8"
                          firstName={event.member.first_name}
                          lastName={event.member.last_name}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          {getEventIcon(event.event_type)}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      {event.created_by && (
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {event.created_by.first_name} {event.created_by.last_name}
                        </span>
                      )}
                      {event.isOptimistic && <span className="text-xs text-primary-500">({t('pending')})</span>}
                      <Badge color={getEventBadgeColor(event.event_type)} className="text-[10px] flex-shrink-0">
                        {getEventLabel(event.event_type)}
                      </Badge>
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                      {(event as any).is_pinned && (
                        <PiPushPinLight className="w-3.5 h-3.5 text-amber-500" />
                      )}
                      {(event as any).is_edited && (
                        <span className="text-gray-400 dark:text-gray-500">{t('edited')}</span>
                      )}
                      {event.timestamp && (
                        <>
                          <PiClockLight className="w-3.5 h-3.5" />
                          <span>{formatTimestamp(event.timestamp)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Card body: message content */}
                  <div className="px-4 pb-3">
                    <div className="text-sm text-gray-700 dark:text-gray-300 break-words whitespace-pre-wrap leading-relaxed">
                      {renderMessageWithMentions(event.message)}
                    </div>
                  </div>

                  {/* Comments section */}
                  {(pendingComments.length > 0 || (event.comments && event.comments.length > 0)) && (
                    <div className="mx-4 mb-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 p-2 space-y-1.5">
                      {/* Existing comments */}
                      {event.comments && event.comments.map((comment, commentIdx) => {
                        const cOwnerId = comment.created_by?.id ?? comment.created_by_user_id;
                        const cIsOwn = !!currentUser && cOwnerId !== undefined && String(cOwnerId) === String(currentUser.id);
                        return (
                        <motion.div
                          key={comment.id || `comment-${commentIdx}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`flex w-full ${cIsOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`w-full sm:w-[80%] px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border ${
                            cIsOwn
                              ? 'border-primary-300 dark:border-primary-700/60'
                              : 'border-gray-200 dark:border-gray-700/60'
                          }`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                {comment.created_by && (
                                  <>
                                    <ProfileImage
                                      profilePicture={comment.created_by.profile_picture || null}
                                      email={comment.created_by.email}
                                      initials={`${comment.created_by.first_name?.[0] || ''}${comment.created_by.last_name?.[0] || ''}`.toUpperCase()}
                                      size="w-5 h-5"
                                    />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                      {comment.created_by.first_name} {comment.created_by.last_name}
                                    </span>
                                  </>
                                )}
                              </div>
                              {comment.timestamp && (
                                <div className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                                  <PiClockLight className="w-3 h-3" />
                                  <span>{formatTimestamp(comment.timestamp)}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300 break-words pl-7">
                              {renderMessageWithMentions(comment.message)}
                            </div>
                          </div>
                        </motion.div>
                        );
                      })}

                      {/* Optimistic (pending) comments — only ones not yet confirmed by server */}
                      <AnimatePresence>
                        {pendingComments.map((comment) => {
                          const cOwnerId = comment.created_by?.id ?? comment.created_by_user_id;
                          const cIsOwn = !!currentUser && cOwnerId !== undefined && String(cOwnerId) === String(currentUser.id);
                          return (
                          <motion.div
                            key={comment.id}
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className={`flex w-full ${cIsOwn ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`w-full sm:w-[80%] px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border opacity-90 ${
                              cIsOwn
                                ? 'border-primary-300 dark:border-primary-700/60'
                                : 'border-gray-200 dark:border-gray-700/60'
                            }`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  {comment.created_by && (
                                    <>
                                      <ProfileImage
                                        profilePicture={comment.created_by.profile_picture || null}
                                        email={comment.created_by.email}
                                        initials={`${comment.created_by.first_name?.[0] || ''}${comment.created_by.last_name?.[0] || ''}`.toUpperCase()}
                                        size="w-5 h-5"
                                      />
                                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {comment.created_by.first_name} {comment.created_by.last_name}
                                      </span>
                                    </>
                                  )}
                                  <span className="text-[10px] text-primary-500">({t('pending')})</span>
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 break-words pl-7">
                                {renderMessageWithMentions(comment.message)}
                              </div>
                            </div>
                          </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Add comment section */}
                  {onCommentAdd && (
                    <div className="px-4 pb-3">
                      {commentingEventId === eventId ? (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-2"
                        >
                          <MentionInput
                            value={eventComments[eventId] || ''}
                            onChange={(value) => setEventComments(prev => ({ ...prev, [eventId]: value }))}
                            onMentionQuery={(query) => {
                              setCommentMentionQuery(query);
                              setShowCommentMentions(query.length > 0);
                            }}
                            placeholder={t('Write a reply... (type @ to mention)')}
                            rows={2}
                            showSuggestions={showCommentMentions}
                            suggestions={filteredCommentMembers}
                            onSuggestionSelect={(memberId, memberName) => {
                              const currentComment = eventComments[eventId] || '';
                              const lastAtIndex = currentComment.lastIndexOf('@');
                              const beforeMention = currentComment.substring(0, lastAtIndex);
                              const afterQuery = currentComment.substring(lastAtIndex).split(/\s/);
                              const afterMention = afterQuery.slice(1).join(' ');
                              const newComment = beforeMention + `@[${memberName}] ` + (afterMention ? afterMention + ' ' : '');
                              setEventComments(prev => ({ ...prev, [eventId]: newComment }));
                              setShowCommentMentions(false);
                              setCommentMentionQuery('');
                            }}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              size="small"
                              onClick={() => {
                                setCommentingEventId(undefined);
                                setEventComments(prev => {
                                  const updated = { ...prev };
                                  delete updated[eventId];
                                  return updated;
                                });
                              }}
                            >
                              {t('Cancel')}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleAddComment(eventId, eventComments[eventId])}
                              disabled={!eventComments[eventId]?.trim()}
                            >
                              {t('Reply')}
                            </Button>
                          </div>
                        </motion.div>
                      ) : (
                        <button
                          onClick={() => setCommentingEventId(eventId)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                        >
                          <PiChatCircleLight className="w-4 h-4" />
                          {t('Reply')}
                          {((event.comments?.length || 0) + pendingComments.length) > 0 && (
                            <span className="text-gray-400 dark:text-gray-500">· {(event.comments?.length || 0) + pendingComments.length}</span>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {t('No activity yet')}
          </div>
        )}
      </div>

      {/* Post Event Section - Sticky at bottom */}
      {onPostEvent && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-shrink-0 p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-t-lg"
        >
          <MentionInput
            value={postMessage}
            onChange={(value) => setPostMessage(value)}
            onMentionQuery={(query) => {
              setMentionQuery(query);
              setShowMentionSuggestions(query.length > 0);
              setSelectedMentionIndex(0);
            }}
            onSubmit={handlePostEvent}
            placeholder={t('What\'s new? Type @ to mention team members')}
            rows={3}
            showSuggestions={showMentionSuggestions}
            suggestions={filteredMembers}
            onSuggestionSelect={insertMention}
            selectedSuggestionIndex={selectedMentionIndex}
            onSelectedIndexChange={setSelectedMentionIndex}
          />
          <div className="flex items-center justify-between gap-2 mt-3">
            <Menu as="div" className="relative">
              <Menu.Button className="flex gap-1 items-center px-2 py-1 border border-gray-300 dark:border-gray-500 bg-transparent text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 rounded shadow-sm text-xs">
                <span className="text-gray-900 dark:text-white font-medium text-xs">{formatEventType(postType)}</span>
                <PiCaretDownLight className="w-3 h-3 text-gray-600 dark:text-gray-300" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Menu.Items className="absolute left-0 bottom-full mb-2 w-40 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50 max-h-64 overflow-y-auto">
                  {['update', 'action', 'milestone', 'issue', 'note'].map((eventType) => (
                    <Menu.Item key={eventType}>
                      {({ active }) => (
                        <div
                          onClick={() => setPostType(eventType)}
                          className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${
                            active ? 'bg-gray-100 dark:bg-gray-600' : ''
                          } ${postType === eventType ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200 font-medium' : ''}`}
                        >
                          {formatEventType(eventType)}
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  setPostMessage('');
                  setPostType('update');
                }}
                disabled={!postMessage.trim() || posting}
              >
                {t('Cancel')}
              </Button>
              <Button
                size="small"
                onClick={handlePostEvent}
                disabled={!postMessage.trim() || posting}
                isLoading={posting}
              >
                <PiArrowUpLight className="w-4 h-4 mr-1" />
                {posting ? t('Posting...') : t('Post')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default FeedComponent;
